import React from 'react';
import { Shield, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import { Card } from '../../components/common';

const PrivacyPolicy = () => {
  const lastUpdate = 'Janeiro de 2025';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-400 to-primary-500 rounded-2xl mb-6 shadow-glow">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4 font-display">
            Política de Privacidade
          </h1>
          <p className="text-lg text-gray-600">
            Mara Labs - Plataforma de Automações
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-4">
            <Calendar className="w-4 h-4" />
            <span>Última atualização: {lastUpdate}</span>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Introdução */}
          <Card>
            <Card.Body className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introdução</h2>
              <p className="text-gray-700 leading-relaxed">
                A <strong>Mara Labs</strong> ("nós", "nosso" ou "nossa") está comprometida em proteger 
                sua privacidade. Esta Política de Privacidade explica como coletamos, usamos, divulgamos 
                e protegemos suas informações quando você utiliza nossa plataforma de automações e serviços 
                de chatbot.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Ao utilizar nossos serviços, você concorda com a coleta e uso de informações de acordo 
                com esta política. Se você não concordar com os termos desta política, por favor, não 
                utilize nossos serviços.
              </p>
            </Card.Body>
          </Card>

          {/* Informações Coletadas */}
          <Card>
            <Card.Body className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Informações que Coletamos</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.1 Informações Fornecidas por Você</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Dados de Cadastro:</strong> Nome, e-mail, telefone, razão social, CNPJ</li>
                <li><strong>Dados de Autenticação:</strong> Senha criptografada, tokens de acesso</li>
                <li><strong>Informações de Pagamento:</strong> Dados de cobrança e faturas</li>
                <li><strong>Dados de Comunicação:</strong> Mensagens trocadas através de nossos chatbots</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.2 Informações Coletadas Automaticamente</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Dados de Uso:</strong> Interações com a plataforma, horários de acesso, funcionalidades utilizadas</li>
                <li><strong>Dados Técnicos:</strong> Endereço IP, tipo de navegador, sistema operacional, cookies</li>
                <li><strong>Dados de Conversas:</strong> Histórico de conversas, métricas de atendimento, protocolos</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.3 Dados de WhatsApp</h3>
              <p className="text-gray-700 leading-relaxed">
                Nossa plataforma integra-se com o WhatsApp Business API para fornecer serviços de automação. 
                Coletamos e processamos:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Números de telefone dos contatos</li>
                <li>Conteúdo das mensagens trocadas via chatbot</li>
                <li>Metadados das conversas (horário, status de entrega, leitura)</li>
                <li>Mídias compartilhadas (imagens, documentos, áudios)</li>
              </ul>
            </Card.Body>
          </Card>

          {/* Uso das Informações */}
          <Card>
            <Card.Body className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Como Usamos suas Informações</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Utilizamos as informações coletadas para:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Fornecer, operar e manter nossos serviços de automação</li>
                <li>Processar e gerenciar conversas através dos chatbots</li>
                <li>Melhorar, personalizar e expandir nossos serviços</li>
                <li>Entender e analisar como você usa nossa plataforma</li>
                <li>Desenvolver novos produtos, serviços e funcionalidades</li>
                <li>Comunicar-nos com você sobre atualizações, suporte técnico e informações promocionais</li>
                <li>Processar transações e enviar notificações relacionadas</li>
                <li>Detectar, prevenir e resolver problemas técnicos e de segurança</li>
                <li>Cumprir obrigações legais e regulatórias</li>
              </ul>
            </Card.Body>
          </Card>

          {/* Compartilhamento */}
          <Card>
            <Card.Body className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Compartilhamento de Informações</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Não vendemos suas informações pessoais. Podemos compartilhar suas informações nas seguintes situações:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.1 Com Provedores de Serviços</h3>
              <p className="text-gray-700 leading-relaxed">
                Compartilhamos dados com terceiros que nos ajudam a operar nossa plataforma:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Meta (WhatsApp Business API):</strong> Para processamento de mensagens</li>
                <li><strong>Provedores de Cloud:</strong> Para hospedagem e armazenamento de dados</li>
                <li><strong>Processadores de Pagamento:</strong> Para processar transações financeiras</li>
                <li><strong>Ferramentas de Analytics:</strong> Para análise de uso e melhorias</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.2 Por Requisitos Legais</h3>
              <p className="text-gray-700 leading-relaxed">
                Podemos divulgar suas informações quando exigido por lei ou em resposta a solicitações 
                legais de autoridades públicas.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.3 Transferências Empresariais</h3>
              <p className="text-gray-700 leading-relaxed">
                Em caso de fusão, aquisição ou venda de ativos, suas informações podem ser transferidas 
                para a entidade sucessora.
              </p>
            </Card.Body>
          </Card>

          {/* Segurança */}
          <Card>
            <Card.Body className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Segurança dos Dados</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Criptografia de dados em trânsito (SSL/TLS) e em repouso</li>
                <li>Autenticação de dois fatores para acesso à plataforma</li>
                <li>Controles de acesso baseados em funções (RBAC)</li>
                <li>Monitoramento contínuo de segurança e detecção de ameaças</li>
                <li>Backups regulares e planos de recuperação de desastres</li>
                <li>Auditorias de segurança periódicas</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Embora façamos nosso melhor esforço para proteger suas informações, nenhum método de 
                transmissão pela internet é 100% seguro. Não podemos garantir segurança absoluta.
              </p>
            </Card.Body>
          </Card>

          {/* Retenção de Dados */}
          <Card>
            <Card.Body className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Retenção de Dados</h2>
              <p className="text-gray-700 leading-relaxed">
                Retemos suas informações pessoais apenas pelo tempo necessário para cumprir os propósitos 
                descritos nesta política, a menos que um período de retenção mais longo seja exigido ou 
                permitido por lei.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Períodos de retenção típicos:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Dados de conversas:</strong> 2 anos após a última interação</li>
                <li><strong>Dados de conta:</strong> Enquanto a conta estiver ativa + 5 anos</li>
                <li><strong>Dados financeiros:</strong> 5 anos conforme legislação fiscal</li>
                <li><strong>Logs de sistema:</strong> 6 meses</li>
              </ul>
            </Card.Body>
          </Card>

          {/* Direitos do Usuário */}
          <Card>
            <Card.Body className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Seus Direitos (LGPD)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                De acordo com a Lei Geral de Proteção de Dados (LGPD), você tem os seguintes direitos:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Confirmação e Acesso:</strong> Confirmar se tratamos seus dados e acessá-los</li>
                <li><strong>Correção:</strong> Solicitar correção de dados incompletos, inexatos ou desatualizados</li>
                <li><strong>Anonimização ou Bloqueio:</strong> Solicitar anonimização ou bloqueio de dados desnecessários</li>
                <li><strong>Eliminação:</strong> Solicitar eliminação de dados tratados com seu consentimento</li>
                <li><strong>Portabilidade:</strong> Receber seus dados em formato estruturado e interoperável</li>
                <li><strong>Informação:</strong> Saber com quem compartilhamos seus dados</li>
                <li><strong>Revogação do Consentimento:</strong> Retirar seu consentimento a qualquer momento</li>
                <li><strong>Oposição:</strong> Se opor ao tratamento em certas situações</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Para exercer qualquer um desses direitos, entre em contato conosco através dos canais 
                indicados na seção "Contato".
              </p>
            </Card.Body>
          </Card>

          {/* Cookies */}
          <Card>
            <Card.Body className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies e Tecnologias Similares</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Utilizamos cookies e tecnologias similares para:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Manter você conectado à plataforma</li>
                <li>Lembrar suas preferências e configurações</li>
                <li>Analisar o uso da plataforma e melhorar a experiência</li>
                <li>Fornecer conteúdo personalizado</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Você pode controlar o uso de cookies através das configurações do seu navegador, mas 
                isso pode afetar a funcionalidade da plataforma.
              </p>
            </Card.Body>
          </Card>

          {/* Menores de Idade */}
          <Card>
            <Card.Body className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Menores de Idade</h2>
              <p className="text-gray-700 leading-relaxed">
                Nossos serviços não são direcionados a menores de 18 anos. Não coletamos intencionalmente 
                informações pessoais de menores de idade. Se você é pai ou responsável e acredita que 
                seu filho nos forneceu informações pessoais, entre em contato conosco para que possamos 
                tomar as medidas necessárias.
              </p>
            </Card.Body>
          </Card>

          {/* Links Externos */}
          <Card>
            <Card.Body className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Links para Sites de Terceiros</h2>
              <p className="text-gray-700 leading-relaxed">
                Nossa plataforma pode conter links para sites de terceiros. Não somos responsáveis pelas 
                práticas de privacidade desses sites. Recomendamos que você leia as políticas de privacidade 
                de qualquer site que visitar.
              </p>
            </Card.Body>
          </Card>

          {/* Alterações */}
          <Card>
            <Card.Body className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Alterações nesta Política</h2>
              <p className="text-gray-700 leading-relaxed">
                Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos você sobre 
                quaisquer alterações publicando a nova política nesta página e atualizando a data de 
                "Última atualização". Recomendamos que você revise esta política periodicamente.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Alterações significativas serão comunicadas por e-mail ou através de notificação destacada 
                em nossa plataforma.
              </p>
            </Card.Body>
          </Card>

          {/* Contato */}
          <Card className="bg-gradient-to-br from-primary-50 to-white border-primary-100">
            <Card.Body>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contato</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Se você tiver dúvidas sobre esta Política de Privacidade ou desejar exercer seus direitos, 
                entre em contato conosco:
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">E-mail</p>
                    <a href="mailto:maralabssoftware@gmail.com" className="text-primary-600 hover:underline">
                      maralabssoftware@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Telefone</p>
                    <a href="tel:+5598988888888" className="text-primary-600 hover:underline">
                      (98) 98888-8888
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Endereço</p>
                    <p className="text-gray-700">
                      Mara Labs Tecnologia LTDA<br />
                      São Luís, Maranhão, Brasil
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-white rounded-lg border border-primary-200">
                <p className="text-sm text-gray-700">
                  <strong>Encarregado de Dados (DPO):</strong><br />
                  Para questões específicas sobre proteção de dados, entre em contato com nosso 
                  Encarregado de Dados através do e-mail: <a href="mailto:maralabssoftware@gmail.com" className="text-primary-600 hover:underline">maralabssoftware@gmail.com</a>
                </p>
              </div>
            </Card.Body>
          </Card>

          {/* Footer */}
          <div className="text-center py-8 text-sm text-gray-600">
            <p>© 2025 Mara Labs Tecnologia LTDA. Todos os direitos reservados.</p>
            <p className="mt-2">CNPJ: 61.685.303/0001-80</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;